// Constants for FormBuilder sub-page types and component types
export const FormBuilderSubPageType = ["Edit Record", "New Record"];
export const FormBuilderComponentType = ["Form"];

// Check if the form builder has changed compared to default form builder
export function isStepsChanged(steps, defaultSteps) {
    if (steps.length !== defaultSteps.length) return true;
    for (let index = 0; index < steps.length; index++) {
        const step = steps[index];
        const defaultStep = defaultSteps[index];
        if (step.name !== defaultStep.name) {
            return true;
        }
        if (isLanguagesChanged(step.languages, defaultStep.languages, 'name')) {
            return true;
        }
        const isChanged = isLayoutChanged(step.layout, defaultStep.layout);
        if (isChanged) {
            return true;
        }
    }
    return false;
}

// Check if the form actions have changed compared to default form actions
export function isFormActionsChanged(formActions, defaultFormActions) {
    if (formActions.length !== defaultFormActions.length) return true;
    for (let index = 0; index < formActions.length; index++) {
        const formAction = formActions[index];
        const defaultFormAction = defaultFormActions[index];
        if (
            formAction.name !== defaultFormAction.name ||
            formAction.isActionHasIcon !== defaultFormAction.isActionHasIcon ||
            formAction.icon !== defaultFormAction.icon ||
            isLanguagesChanged(formAction.languages, defaultFormAction.languages, 'label')
        ) {
            return true;
        }
    }
    return false;
}

// Check if the languages have changed compared to default languages
function isLanguagesChanged(languages = {}, defaultLanguages = {}, keyToCompare) {
    const keys = Object.keys(languages);
    const defaultKeys = Object.keys(defaultLanguages);
    if (keys.length !== defaultKeys.length) return true;
    for (const key of keys) {
        if (languages[key][keyToCompare] !== defaultLanguages[key][keyToCompare]) {
            return true;
        }
    }
    return false;
}


// Check if the layout has changed compared to default layouts
export function isLayoutChanged(layouts, defaultLayouts) {

    if (layouts.length !== defaultLayouts.length) return true;
    
    if (areSectionsMoved(layouts, defaultLayouts)) return true;

    for (let index = 0; index < layouts.length; index++) {
        const section = layouts[index];
        const defaultSection = defaultLayouts.find(defaultSection => defaultSection.section === section.section);

        if (areSectionsChanged(section, defaultSection)) {
            return true;
        }

        const columns = section.columns;
        const defaultColumns = defaultSection && defaultSection.columns;

        if (columns.length !== defaultColumns.length) return true;

        for (let index = 0; index < columns.length; index++) {
            const column = columns[index];
            const defaultColumn = defaultColumns.find(defCol => column.attributeId === defCol.attributeId);
            if (areColumnsChanged(column, defaultColumn)) {
                return true;
            }
        }
    }

    return false;
}

// Check if the sections are moved
function areSectionsMoved(layout, defaultLayout) {
    const layoutSections = layout.map((section) => section.section);
    const defaultLayoutSections = defaultLayout.map((section) => section.section);
    return layoutSections.some((section, index) => section !== defaultLayoutSections[index]);
}

// Check if the sections are changed
function areSectionsChanged(section, defaultSection) {
    return (
        !defaultSection ||
        section.section !== defaultSection.section ||
        section.columns.length !== defaultSection.columns.length ||
        isLanguagesChanged(section.languages, defaultSection.languages, 'section')
    );
}

// Check if the columns are changed
function areColumnsChanged(column, defaultColumn) {
    return (
        !defaultColumn ||
        column.w !== defaultColumn.w ||
        column.h !== defaultColumn.h ||
        column.x !== defaultColumn.x ||
        column.y !== defaultColumn.y ||
        column.cellRules !== defaultColumn.cellRules ||
        column.hyperlink !== defaultColumn.hyperlink ||
        column.targetPageId !== defaultColumn.targetPageId
    );
}

// Find a dragged column by its ID within sections
export function findDraggedColumn(sections, draggedColumnId) {
    for (const section of sections) {
        const draggedItem = section.columns.find(column => column.i === draggedColumnId);
        if (draggedItem) return draggedItem;
    }
}

// Find a dragged attribute by its ID within attributes
export function findDraggedAttribute(attributes, draggedAttribute) {
    return attributes.find(attribute => attribute._id === draggedAttribute.attributeId);
}

// Find the section ID containing a dragged column within sections
export function findDraggedColumnSectionId(sections, draggedColumn) {
    return sections.find(section => section.columns.includes(draggedColumn)).id;
}

// Get attributes from a layout based on IDs and entity ID
export function getAttributesFromLayout(layout, attributes, entityId) {
    const componentAttributesIds = [];

    layout.forEach(section => {
        section.columns.forEach(column => componentAttributesIds.push(column.attributeId));
    });

    const componentAttributes = attributes
        .filter(attribute => componentAttributesIds.includes(attribute._id))
        .map(attribute => ({ ...attribute, attributeId: attribute._id, entityId }));
    return componentAttributes || [];
};